import clsx from 'clsx';
import type { ChangeEvent, JSX } from 'react';
import type { AvailabilityFiltersState, RangeBounds } from '../availabilitiesFilters';
import { formatLabel, getRangeError } from '../availabilitiesFilters';
import type { AvailabilitiesFilterControlsContent } from '@/lib/types/siteSettings';
import type { AssetType } from '@/lib/types/property';
import { ASSET_TYPE_LABELS } from '@/lib/constants/propertyOptions';
import { formatPublicCurrency } from '@/lib/utils/publicCurrency';
import { RangeSliderGroup } from './RangeSliderGroup';
import { RangeNumberInputGroup } from './RangeNumberInputGroup';

export function AvailabilitiesFilterControls({
  value,
  onChange,
  assetTypeOptions,
  content,
  monthlyRentBounds,
}: {
  value: AvailabilityFiltersState;
  onChange: (next: AvailabilityFiltersState) => void;
  assetTypeOptions: string[];
  content: AvailabilitiesFilterControlsContent;
  monthlyRentBounds: RangeBounds | null;
}): JSX.Element {
  const rentRangeError = getRangeError(value.monthlyRent.min, value.monthlyRent.max);
  const areaRangeError = getRangeError(value.areaSf.min, value.areaSf.max);
  const rentErrorId = rentRangeError !== null ? 'availability-rent-range-error' : undefined;
  const areaErrorId = areaRangeError !== null ? 'availability-area-range-error' : undefined;
  const formatRentValue = (amount: number) => `${formatPublicCurrency(amount)}/mo`;
  const resolveSliderValue = (candidate: number | null, fallback: number) =>
    typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : fallback;
  const formatValueOrPlaceholder = (
    candidate: number | null,
    formatValue: (value: number) => string,
  ) => (candidate === null ? content.placeholder : formatValue(candidate));
  const rentMinBound = monthlyRentBounds?.min ?? 0;
  const rentMaxBound = monthlyRentBounds?.max ?? 0;
  const rentMinValue = resolveSliderValue(value.monthlyRent.min, rentMinBound);
  const rentMaxValue = resolveSliderValue(value.monthlyRent.max, rentMaxBound);
  const rentMinLabel = formatValueOrPlaceholder(value.monthlyRent.min, formatRentValue);
  const rentMaxLabel = formatValueOrPlaceholder(value.monthlyRent.max, formatRentValue);
  const rentBoundsLabel = monthlyRentBounds
    ? `${formatRentValue(rentMinBound)} – ${formatRentValue(rentMaxBound)}`
    : '';
  const handleRangeChange =
    (scope: 'monthlyRent', key: 'min' | 'max', bounds: RangeBounds | null) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!bounds) {
        return;
      }
      const rawValue = Number(event.target.value);
      if (!Number.isFinite(rawValue)) {
        return;
      }
      const currentScope = value[scope];
      const minBound = bounds.min;
      const maxBound = bounds.max;
      const currentMin = currentScope.min ?? minBound;
      const currentMax = currentScope.max ?? maxBound;
      let nextValue = Math.min(Math.max(rawValue, minBound), maxBound);

      if (key === 'min') {
        nextValue = Math.min(nextValue, currentMax);
      } else {
        nextValue = Math.max(nextValue, currentMin);
      }

      const normalized =
        key === 'min'
          ? nextValue <= minBound
            ? null
            : nextValue
          : nextValue >= maxBound
            ? null
            : nextValue;

      onChange({
        ...value,
        [scope]: {
          ...currentScope,
          [key]: normalized,
        },
      });
    };

  const togglePermittedUse = (assetType: string) => {
    const next = value.permittedUses.includes(assetType)
      ? value.permittedUses.filter((entry) => entry !== assetType)
      : [...value.permittedUses, assetType];

    onChange({
      ...value,
      permittedUses: next.sort(),
    });
  };

  return (
    <div className="flex flex-col gap-6" data-ui="Public.Availabilities.Filters">
      <RangeSliderGroup
        title={content.priceTitle}
        description={content.priceDescription}
        minLabel={content.minLabel}
        maxLabel={content.maxLabel}
        minDisplay={rentMinLabel}
        maxDisplay={rentMaxLabel}
        minValue={rentMinValue}
        maxValue={rentMaxValue}
        minBound={rentMinBound}
        maxBound={rentMaxBound}
        disabled={!monthlyRentBounds}
        onMinChange={handleRangeChange('monthlyRent', 'min', monthlyRentBounds)}
        onMaxChange={handleRangeChange('monthlyRent', 'max', monthlyRentBounds)}
        showMinHandle={false}
        rangeLabel={monthlyRentBounds ? rentBoundsLabel : undefined}
        error={rentRangeError}
        errorId={rentErrorId}
      />

      <RangeNumberInputGroup
        title={content.sizeTitle}
        description={content.sizeDescription}
        minLabel={content.minLabel}
        maxLabel={content.maxLabel}
        placeholder={content.placeholder}
        suffix="sf"
        minValue={value.areaSf.min}
        maxValue={value.areaSf.max}
        disabled={false}
        onMinChange={(nextValue) => {
          onChange({
            ...value,
            areaSf: {
              ...value.areaSf,
              min: nextValue,
            },
          });
        }}
        onMaxChange={(nextValue) => {
          onChange({
            ...value,
            areaSf: {
              ...value.areaSf,
              max: nextValue,
            },
          });
        }}
        error={areaRangeError}
        errorId={areaErrorId}
      />

      {assetTypeOptions.length > 0 ? (
        <div className="rounded-2xl border border-surface-subtle bg-surface-subtle/40 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
            {content.usesTitle}
          </p>
          <p className="mt-1 text-sm text-text-primary/70">{content.usesDescription}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {assetTypeOptions.map((option) => {
              const isActive = value.permittedUses.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => {
                    togglePermittedUse(option);
                  }}
                  className={clsx(
                    'min-h-11 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide transition',
                    isActive
                      ? 'bg-brand-primary text-white shadow-sm'
                      : 'border border-surface-subtle bg-white text-text-primary hover:border-brand-primary/40 hover:bg-brand-primary/5',
                  )}
                >
                  {formatAssetTypeOption(option)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function formatAssetTypeOption(value: string): string {
  if (value in ASSET_TYPE_LABELS) {
    return ASSET_TYPE_LABELS[value as AssetType];
  }
  return formatLabel(value);
}
